/**
 * The family filter, shared by every table that has a familyId to filter on.
 *
 * Three things here are deliberate.
 *
 * The list is fetched once per page load and memoised at module scope. It is on
 * a dozen screens; refetching it per screen would triple the request count for a
 * two-row table.
 *
 * If the family list fails to load, the control degrades to a free-text
 * familyId box rather than disappearing. An operator who arrived here from the
 * database console already has the id in their clipboard, and losing the filter
 * because a *different* endpoint is down is the kind of cascading uselessness
 * that makes people stop opening the tool.
 *
 * And there is a "no family" option. `veo-test-2` is a real commission row with
 * no `familyId` and no `createdAt`, so it is absent from every byFamily GSI and
 * invisible to the family's own reads. The rows with no family are exactly the
 * rows nobody is looking at, so they get a filter of their own.
 */
import { useEffect, useState } from 'react';
import { listFamilies, type Family } from '../api';
import { message } from './async';

/** Sentinel filter value meaning "rows that have no familyId at all". Never
 *  sent to the server — no endpoint understands it — so it is applied here. */
export const NO_FAMILY = '#none';

let cache: Promise<Family[]> | null = null;

function load(): Promise<Family[]> {
  if (!cache) {
    cache = listFamilies()
      .then((page) => page.rows)
      .catch((err: unknown) => {
        cache = null; // let a later screen retry rather than caching the failure
        throw err;
      });
  }
  return cache;
}

export function useFamilies(): { families: Family[] | null; error: string } {
  const [families, setFamilies] = useState<Family[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    load().then(
      (rows) => live && setFamilies(rows),
      (err: unknown) => live && setError(message(err)),
    );
    return () => {
      live = false;
    };
  }, []);
  return { families, error };
}

export function familyLabel(families: Family[] | null, familyId?: string | null): string {
  if (!familyId) return '';
  const found = families?.find((f) => f.familyId === familyId);
  return found?.name || '';
}

export function FamilyFilter({
  value,
  onChange,
  disabledReason,
}: {
  value: string;
  onChange: (next: string) => void;
  /** When this table structurally cannot be filtered by family. Rendered as a
   *  disabled control with the reason, rather than omitted — an absent control
   *  reads as an oversight, and this one is a property of the data. */
  disabledReason?: string;
}) {
  const { families, error } = useFamilies();

  if (disabledReason) {
    return (
      <span className="group">
        <label>Family</label>
        <select disabled title={disabledReason}>
          <option>not filterable</option>
        </select>
        <span className="muted small">{disabledReason}</span>
      </span>
    );
  }

  if (error || (families && families.length === 0)) {
    return (
      <span className="group">
        <label>Family</label>
        <input
          placeholder="familyId"
          value={value === NO_FAMILY ? '' : value}
          onChange={(e) => onChange(e.target.value.trim())}
        />
        <span className="muted small">
          {error ? `family list unavailable (${error}) — paste an id` : 'no families'}
        </span>
      </span>
    );
  }

  return (
    <span className="group">
      <label htmlFor="family-filter">Family</label>
      <select
        id="family-filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!families}
      >
        <option value="">{families ? 'every family' : 'loading…'}</option>
        {(families ?? []).map((f) => (
          <option key={f.familyId} value={f.familyId}>
            {f.name || f.familyId.slice(0, 8)}
            {f.unlimitedCredits ? ' (unlimited)' : ''}
          </option>
        ))}
        <option value={NO_FAMILY}>— rows with no family —</option>
      </select>
    </span>
  );
}

/**
 * Apply the family filter in the browser.
 *
 * Every list endpoint is passed `familyId` as a query parameter AND filtered
 * here. Belt and braces on purpose: the staff API is gaining these parameters
 * while this portal is being written, and an endpoint that quietly ignores an
 * unknown query string would otherwise show an operator every family's rows
 * under one family's name. If the server did honour it, this is a no-op.
 *
 * The cost of that safety is real and has to be said on screen: filtering after
 * a capped scan can report "nothing matched" when the match was simply beyond
 * the cap. `CapNote` is what says so.
 */
export function byFamily<T extends { familyId?: string | null }>(
  rows: T[],
  familyId: string,
): T[] {
  if (!familyId) return rows;
  if (familyId === NO_FAMILY) return rows.filter((row) => !row.familyId);
  return rows.filter((row) => row.familyId === familyId);
}

/**
 * Turn the filter into the `family` query parameter the API understands.
 *
 * `/admin/clips` is the one endpoint that has a word for "no family":
 * `?family=none` is the shared pool, which is 123 of 133 assets and the normal
 * case rather than an anomaly. Everywhere else the sentinel has no server-side
 * equivalent and must not be sent, because a handler comparing `familyId` to the
 * literal string "#none" would return an empty list that looks like an answer.
 */
export function serverFamily(
  value: string,
  options: { noneParam?: string } = {},
): string | undefined {
  if (value === NO_FAMILY) return options.noneParam;
  return value || undefined;
}
